import { z } from "zod";

/**
 * The two locations seeded into every environment. Protected for the same
 * reason as the role classifications: `seedWorkLocations` re-inserts them, so a
 * delete would appear to work and then undo itself on the next boot.
 */
const PROTECTED_WORK_LOCATION_KEYS = ["us", "intl"] as const;

export function isProtectedWorkLocationKey(key: string): boolean {
  return (PROTECTED_WORK_LOCATION_KEYS as readonly string[]).includes(key);
}

/** Lowercase snake_case — stored on `person.location` and in audit diffs. */
const workLocationKey = z
  .string()
  .trim()
  .min(1, "Key is required")
  .max(60, "Key is too long")
  .regex(/^[a-z][a-z0-9_]*$/, "Use lowercase letters, numbers and underscores, e.g. emea");

// Default-free base — see the zod-v4 note in `role-classifications.ts`.
const workLocationFieldsSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(120, "Name is too long"),
  isActive: z.boolean().optional(),
});

export const workLocationCreateSchema = workLocationFieldsSchema.extend({
  key: workLocationKey,
});

/** `key` is absent by design — immutable once created. */
export const workLocationUpdateSchema = workLocationFieldsSchema.partial();

export const workLocationReorderSchema = z.object({
  direction: z.enum(["up", "down"]),
});

export type WorkLocationCreateInput = z.infer<typeof workLocationCreateSchema>;
export type WorkLocationUpdateInput = z.infer<typeof workLocationUpdateSchema>;
